// QR-code modal shown after a successful bill completion. Lets the cashier
// generate a re-order link (via POST /communications/reorder-link) and either
// display the QR for the customer to scan on their phone, copy the link, or
// send via WhatsApp/Email deep-link.
//
// G4.3: the ?src=qr query param lets analytics distinguish QR-scan conversions
// from plain-link clicks without a second endpoint.

import { useCallback, useState } from 'react';
import QRCode from 'qrcode';
import { Button, Input, GLASS, cn, useToast } from '@bbpos/ui';
import { communicationsService } from '@/services/communications.service';
import { ApiError } from '@/lib/api-client';
import { useTenantDial } from '@/hooks/use-tenant-dial';

type Channel = 'WHATSAPP' | 'EMAIL';

export interface ReorderQRModalProps {
  billId: string;
  billNumber: string;
  defaultPhone?: string | null;
  defaultEmail?: string | null;
  onClose: () => void;
}

function isValidPhone(v: string): boolean {
  return /^\+[1-9]\d{6,14}$/.test(v);
}
function isValidEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

function addQrSrc(link: string): string {
  const sep = link.includes('?') ? '&' : '?';
  return `${link}${sep}src=qr`;
}

export function ReorderQRModal({
  billId,
  billNumber,
  defaultPhone,
  defaultEmail,
  onClose,
}: ReorderQRModalProps) {
  const { toast } = useToast();

  const dial = useTenantDial();
  const seedChannel: Channel = defaultPhone ? 'WHATSAPP' : 'EMAIL';
  const [channel, setChannel] = useState<Channel>(seedChannel);
  const [recipient, setRecipient] = useState<string>(defaultPhone ?? defaultEmail ?? '');
  const [link, setLink] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recipientValid = channel === 'WHATSAPP' ? isValidPhone(recipient) : isValidEmail(recipient);

  const generate = useCallback(async () => {
    if (!recipientValid) {
      setError(channel === 'WHATSAPP' ? 'Phone must be in E.164 format (e.g. +919876543210)' : 'Enter a valid email address');
      return;
    }
    setError(null);
    setIsGenerating(true);
    try {
      const result = await communicationsService.createReorderLink({
        bill_id: billId,
        channel,
        recipient,
      });
      const qrLink = addQrSrc(result.reorder_link);
      setLink(qrLink);
      const dataUrl = await QRCode.toDataURL(qrLink, { width: 256, margin: 1 });
      setQrDataUrl(dataUrl);
      toast({ title: `Re-order link sent via ${channel === 'WHATSAPP' ? 'WhatsApp' : 'Email'}`, variant: 'success' });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to generate re-order link';
      setError(message);
    } finally {
      setIsGenerating(false);
    }
  }, [billId, channel, recipient, recipientValid, toast]);

  // No auto-trigger on mount: creating the link also delivers it to the
  // recipient (PS3-003), so nothing is sent until the cashier presses the button.

  const copyLink = useCallback(async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      toast({ title: 'Link copied to clipboard', variant: 'success' });
    } catch {
      toast({ title: 'Could not copy — copy manually', variant: 'error' });
    }
  }, [link, toast]);

  const whatsappHref = link && channel === 'WHATSAPP'
    ? `https://wa.me/${recipient.replace(/^\+/, '')}?text=${encodeURIComponent(`Re-order your last purchase: ${link}`)}`
    : null;
  const emailHref = link && channel === 'EMAIL'
    ? `mailto:${recipient}?subject=${encodeURIComponent('Re-order your last purchase')}&body=${encodeURIComponent(`Tap to re-order: ${link}`)}`
    : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Share re-order QR"
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/60 backdrop-blur-sm p-4 animate-fade-in"
      onClick={onClose}
    >
      <div
        className={cn(GLASS, 'max-w-md w-full p-6 space-y-4 animate-scale-in')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
              Share re-order QR
            </h2>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 font-mono">
              {billNumber}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800"
            aria-label="Close"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {!link && (
          <>
            <div role="tablist" className="flex gap-2 rounded-xl bg-neutral-100 dark:bg-neutral-800 p-1">
              {(['WHATSAPP', 'EMAIL'] as Channel[]).map((c) => (
                <button
                  key={c}
                  role="tab"
                  aria-selected={channel === c}
                  type="button"
                  onClick={() => {
                    setChannel(c);
                    setRecipient(c === 'WHATSAPP' ? (defaultPhone ?? '') : (defaultEmail ?? ''));
                    setError(null);
                  }}
                  className={cn(
                    'flex-1 rounded-lg px-3 py-1.5 text-sm font-medium transition-all',
                    channel === c
                      ? 'bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 shadow-sm'
                      : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700',
                  )}
                >
                  {c === 'WHATSAPP' ? 'WhatsApp' : 'Email'}
                </button>
              ))}
            </div>

            {channel === 'WHATSAPP' ? (
              <Input
                variant="phone"
                defaultDial={dial}
                label="WhatsApp number"
                placeholder="98765 43210"
                value={recipient}
                onChange={setRecipient}
                error={error ?? undefined}
              />
            ) : (
              <Input
                type="email"
                label="Email address"
                placeholder="customer@example.com"
                value={recipient}
                onChange={setRecipient}
                error={error ?? undefined}
              />
            )}

            <Button
              onClick={generate}
              disabled={!recipientValid || isGenerating}
              loading={isGenerating}
              variant="primary"
              className="w-full"
            >
              Generate & send
            </Button>
          </>
        )}

        {link && qrDataUrl && (
          <div className="space-y-4 animate-fade-in">
            <div className="flex justify-center">
              <div className="rounded-2xl bg-white p-3 shadow-lg">
                <img src={qrDataUrl} alt="Re-order QR code" width={224} height={224} />
              </div>
            </div>
            <p className="text-center text-xs text-neutral-500 dark:text-neutral-400">
              Customer scans this QR to re-order. Also sent via {channel === 'WHATSAPP' ? 'WhatsApp' : 'Email'}.
            </p>

            <div className="flex gap-2">
              <Button onClick={copyLink} variant="secondary" className="flex-1">Copy link</Button>
              {whatsappHref && (
                <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="flex-1">
                  <Button variant="primary" className="w-full">Open WhatsApp</Button>
                </a>
              )}
              {emailHref && (
                <a href={emailHref} className="flex-1">
                  <Button variant="primary" className="w-full">Open Email</Button>
                </a>
              )}
            </div>
            <button
              type="button"
              onClick={() => { setLink(null); setQrDataUrl(null); }}
              className="w-full text-xs text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
            >
              Send to another recipient
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
